import EventEmitter from 'events';

const getQualityLimitationReason = (stats) => {
  const qualityLimitationReasons = stats.video?.layers
    .map(({ qualityLimitationReason }) => qualityLimitationReason).filter(Boolean);
  if (!qualityLimitationReasons?.length) {
    return null;
  }
  if (qualityLimitationReasons.includes('bandwidth')) {
    return 'bandwidth';
  }
  if (qualityLimitationReasons.includes('cpu')) {
    return 'cpu';
  }
  return 'other';
};

const hasCodecChanged = (prevStats, currentStats) =>
  currentStats.video?.layers.some((videoLayer) => {
    if (!videoLayer.codec) {
      return false;
    }
    const prevVideoLayer = prevStats?.video?.layers
      .find(layer => layer.width === videoLayer.width && layer.height === videoLayer.height);
    return prevVideoLayer?.codec && prevVideoLayer.codec !== videoLayer.codec;
  });

const hasResolutionChanged = (prevStats, currentStats) => {
  if (!prevStats) {
    return false;
  }
  if (prevStats.video?.layers.length !== currentStats.video?.layers.length) {
    return true;
  }
  return currentStats.video?.layers.some(videoLayer =>
    !prevStats.video?.layers
      .find(layer => layer.width === videoLayer.width && layer.height === videoLayer.height));
};

const createPublisherVideoQualityWatcher = (getPcStats, statsHelpers, interval = 1000) => {
  const ee = new EventEmitter();
  let lastStats;
  let lastEventReason;

  const processStats = (stats, startTime) => {
    const parsedStats = statsHelpers.normalizeStats(stats, false, startTime);
    const prevLastStats = lastStats;
    lastStats = parsedStats;

    const qualityLimitationReason = getQualityLimitationReason(parsedStats);
    if (qualityLimitationReason) {
      if (lastEventReason === qualityLimitationReason) {
        return;
      }
      lastEventReason = qualityLimitationReason;
      ee.emit('videoQualityChanged', {
        reason: qualityLimitationReason,
        stats: parsedStats,
      });
      return;
    }

    if (hasCodecChanged(prevLastStats, parsedStats)) {
      lastEventReason = null;
      ee.emit('videoQualityChanged', {
        reason: 'codecChange',
        stats: parsedStats,
      });
      return;
    }

    if (hasResolutionChanged(prevLastStats, parsedStats)) {
      lastEventReason = null;
      ee.emit('videoQualityChanged', {
        reason: 'resolutionChange',
        stats: parsedStats,
      });
      return;
    }

    if (lastEventReason) {
      lastEventReason = null;
      ee.emit('videoQualityChanged', {
        reason: 'none',
        stats: parsedStats,
      });
    }
  };

  const getStats = (startTime) => {
    getPcStats((err, stats) => {
      if (!err) {
        processStats(stats, startTime);
      }
    });
  };

  let cancelId;
  return Object.assign(ee, {
    start() {
      if (cancelId) {
        // already started
        return;
      }
      const startTime = Date.now();
      cancelId = setInterval(() => {
        getStats(startTime);
      }, interval);
    },

    stop() {
      clearInterval(cancelId);
      cancelId = undefined;
      lastStats = undefined;
      lastEventReason = undefined;
    },

    destroy() {
      this.stop();
      ee.removeAllListeners();
    },
  });
};

export default createPublisherVideoQualityWatcher;
